import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createAdminGroupChatThread } from '../../../api/adminGroupChat';
import useOwnedMutation from '../../../hooks/useOwnedMutation';
import useRequestOwnership from '../../../hooks/useRequestOwnership';
import EventDerivedGroupModal from './EventDerivedGroupModal';

export type EventDerivedGroupLauncherHandle = { open: () => void };
type Props = {
  eventId?: string;
  eventName?: string;
  viewerId?: string | number;
  allowed: boolean;
  artists: React.ComponentProps<typeof EventDerivedGroupModal>['artists'];
  loadMembers: () => Promise<boolean>;
  navigate: (path: string) => void;
  onSuccess: (message: string) => void;
};

const EventDerivedGroupLauncher = forwardRef<EventDerivedGroupLauncherHandle, Props>(function EventDerivedGroupLauncher(
  { eventId, eventName, viewerId, allowed, artists, loadMembers, navigate, onSuccess }, ref
) {
  const scope = JSON.stringify([eventId, viewerId, allowed]);
  const { begin, invalidate } = useRequestOwnership(scope);
  const mutation = useOwnedMutation(scope);
  const [openScope, setOpenScope] = useState<string | null>(null);
  const open = openScope === scope;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const attempt = useRef<{ body: string; key: string } | null>(null);
  useEffect(() => { setOpenScope(null); setSaving(false); setLoading(false); setError(''); }, [scope]);

  const reload = async () => {
    if (!allowed || !eventId) return;
    const current = begin('members');
    if (!open) attempt.current = null;
    setOpenScope(scope); setLoading(true); setError('');
    try {
      const loaded = await loadMembers();
      if (current() && !loaded) setError('メンバーを読み込めませんでした。再読み込みしてください。');
    } catch (error) {
      if (current()) setError(error instanceof Error ? error.message : 'メンバーを読み込めませんでした。');
    } finally { if (current()) setLoading(false); }
  };
  useImperativeHandle(ref, () => ({ open: () => { void reload(); } }));

  const create = async ({ title, artistIds, iconFile }: { title: string; artistIds: string[]; iconFile: string }) => {
    if (!allowed || !eventId || loading) return;
    const operation = mutation.start();
    if (!operation) return;
    setSaving(true); setError('');
    try {
      const body = { title, mode: 'manual' as const, artistIds, linkedEventUuid: eventId, iconFile };
      const signature = JSON.stringify([scope, body]);
      if (attempt.current?.body !== signature) attempt.current = { body: signature, key: `web-event-group-${crypto.randomUUID()}` };
      const payload = await createAdminGroupChatThread(body, attempt.current.key);
      if (!operation.current()) return;
      const threadId = String(payload?.thread?.thread_id || '').trim();
      if (payload?.success !== true || !threadId) throw new Error('作成結果を確認できませんでした。グループ一覧を確認してください。');
      setOpenScope(null);
      onSuccess('イベントに連動するグループチャットを作成しました。');
      navigate(`/admin/workspaces/communications/group-chat?thread=${encodeURIComponent(threadId)}`);
    } catch (error) {
      if (operation.current()) setError(error instanceof Error ? error.message : 'グループチャットを作成できませんでした。');
    } finally {
      if (operation.current()) setSaving(false);
      operation.finish();
    }
  };

  return <EventDerivedGroupModal key={scope} open={open && allowed} eventName={eventName} artists={artists}
    loading={loading} saving={saving} error={error} onRetry={() => { void reload(); }}
    onClose={() => { invalidate('members'); setOpenScope(null); }} onCreate={create} />;
});
export default EventDerivedGroupLauncher;
